import React, {
    createContext,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react'

export const MAX_SVG_WIDTH = 800

export function getDimensions(width: number) {
    const isMobile = width < 768

    const SVG_WIDTH = Math.min(width, MAX_SVG_WIDTH)
    const MAX_FULL_PLAYERS = isMobile ? 2 : 5
    const MAX_TOTAL_PLAYERS = isMobile ? 9 : 9

    const ADD_BUTTON_DIM = isMobile ? 20 : 25

    const SLIDER_OFFSET_FROM_TEXT = isMobile ? 40 : 100
    const TEXT_RIGHT_SIDE = SLIDER_OFFSET_FROM_TEXT - 10

    const IMG_WIDTH = isMobile ? 52 : 65
    const IMG_TOP_BUFFER = 15
    const BETWEEN_PLAYERS_BUFFER = isMobile ? 5 : 10

    const LOGO_WIDTH = isMobile ? 40 : 55
    const TOP_BUFFER = isMobile ? 100 : 115
    const TITLE_TOP_BUFFER = isMobile ? 7 : 15

    const REMOVE_STAT_OFFSET = isMobile ? 18 : 78
    const SETTINGS_MENU_DIM = isMobile ? 16 : 24
    const SETTINGS_TRIANGLE_PATH = isMobile ? '5,6 11,6 8,10' : '8,9 16,9 12,14'

    const STAT_DRAG_RECT_WIDTH = isMobile ? 126 : 176
    const REMOVE_BUTTON_X = STAT_DRAG_RECT_WIDTH - 4

    return {
        isMobile,
        SVG_WIDTH,
        MAX_FULL_PLAYERS,
        MAX_TOTAL_PLAYERS,
        ADD_BUTTON_DIM,
        TEXT_RIGHT_SIDE,
        SLIDER_OFFSET_FROM_TEXT,
        IMG_WIDTH,
        IMG_TOP_BUFFER,
        BETWEEN_PLAYERS_BUFFER,
        LOGO_WIDTH,
        TOP_BUFFER,
        REMOVE_STAT_OFFSET,
        SETTINGS_MENU_DIM,
        SETTINGS_TRIANGLE_PATH,
        TITLE_TOP_BUFFER,
        STAT_DRAG_RECT_WIDTH,
        REMOVE_BUTTON_X,
    }
}

export type Dimensions = ReturnType<typeof getDimensions>

const DimensionsContext = createContext<Dimensions | null>(null)

export function DimensionsProvider({
    width,
    children,
}: {
    width: number
    children: React.ReactNode
}) {
    const value = useMemo(() => getDimensions(width), [width])
    return (
        <DimensionsContext.Provider value={value}>
            {children}
        </DimensionsContext.Provider>
    )
}

export function useDimensions(): Dimensions {
    const dimensions = useContext(DimensionsContext)
    if (dimensions === null) {
        throw new Error(
            'useDimensions must be used within a DimensionsProvider'
        )
    }
    return dimensions
}

export function useWindowWidth() {
    const [windowWidth, setWindowWidth] = useState(
        typeof window !== 'undefined'
            ? document.documentElement.clientWidth
            : MAX_SVG_WIDTH
    )

    useEffect(() => {
        const handleResize = () =>
            setWindowWidth(document.documentElement.clientWidth)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    return windowWidth
}

export function useContainerWidth(
    ref: React.RefObject<HTMLElement | null>,
    fallback = MAX_SVG_WIDTH
) {
    const [width, setWidth] = useState(fallback)

    useLayoutEffect(() => {
        const element = ref.current
        if (!element || typeof ResizeObserver === 'undefined') {
            return undefined
        }

        const update = () => setWidth(element.clientWidth)
        update()

        const observer = new ResizeObserver(update)
        observer.observe(element)

        return () => observer.disconnect()
    }, [ref])

    return width
}

export default function useResponsiveDimensions() {
    return getDimensions(useWindowWidth())
}
